import React, { useEffect, useState } from "react";
import { Button, Input } from "antd";

const createMathChallenge = () => {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return {
    question: `What is ${a} + ${b}?`,
    answer: a + b,
  };
};

/**
 * Secure deletion confirmation with math verification.
 * A new question is generated each time this step mounts/opens.
 */
export default function SecureDeleteConfirmation({
  entityType = "item",
  displayName = "",
  warningMessage,
  subWarningMessage,
  loading = false,
  onCancel,
  onContinue,
}) {
  const [challenge, setChallenge] = useState(() => createMathChallenge());
  const [answer, setAnswer] = useState("");
  const [showMismatch, setShowMismatch] = useState(false);

  useEffect(() => {
    setChallenge(createMathChallenge());
    setAnswer("");
    setShowMismatch(false);
  }, [displayName, entityType]);

  const isCorrect =
    answer.trim() !== "" && Number(answer.trim()) === challenge.answer;

  const handleChange = (e) => {
    const digitsOnly = (e.target.value || "").replace(/[^\d]/g, "");
    setAnswer(digitsOnly);
    if (digitsOnly.length === 0) {
      setShowMismatch(false);
      return;
    }
    setShowMismatch(Number(digitsOnly) !== challenge.answer);
  };

  const handleContinue = () => {
    if (!isCorrect || loading) return;
    onContinue?.();
  };

  return (
    <div className="secure-delete-confirmation">
      <h2 className="secure-delete-title">Delete {entityType}?</h2>

      <div className="secure-delete-info-block">
        <p className="secure-delete-warning">
          {warningMessage ||
            `Are you sure you want to delete this ${entityType}? This action is permanent and cannot be undone.`}
        </p>
        {subWarningMessage ? (
          <p className="secure-delete-warning secure-delete-warning-secondary">
            {subWarningMessage}
          </p>
        ) : null}

        <div className="secure-delete-entity-row">
          <span className="secure-delete-entity-label">{entityType} Name</span>
          <span className="secure-delete-entity-value" title={displayName}>
            {displayName}
          </span>
        </div>
      </div>

      <div className="secure-delete-verify-block">
        <label className="secure-delete-label" htmlFor="secure-delete-math-input">
          {challenge.question}
        </label>
        <Input
          id="secure-delete-math-input"
          className="secure-delete-input"
          value={answer}
          onChange={handleChange}
          placeholder="Enter your answer"
          inputMode="numeric"
          disabled={loading}
          autoFocus
          aria-invalid={showMismatch}
          aria-describedby={showMismatch ? "secure-delete-math-error" : undefined}
          onPressEnter={handleContinue}
        />
        {showMismatch ? (
          <p id="secure-delete-math-error" className="secure-delete-error" role="alert">
            Incorrect answer. Please try again.
          </p>
        ) : null}
      </div>

      <div className="secure-delete-actions">
        <Button
          className="secure-delete-cancel-btn"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          className="secure-delete-continue-btn"
          onClick={handleContinue}
          disabled={!isCorrect || loading}
          loading={loading}
        >
          Delete
        </Button>
      </div>
    </div>
  );
}
